// Haptic feedback — platform-aware wrapper.
//
// On the web (Chrome Android, any browser with navigator.vibrate): uses the
// Vibration API. iOS Safari and most desktops silently no-op, which is fine.
//
// Under Capacitor on iOS: uses @capacitor/haptics (CoreHaptics under the
// hood) because navigator.vibrate is permanently unavailable in WKWebView.
// This is the only way to produce real tactile feedback on iPhone.
//
// We respect the same mute toggle as audio so the settings surface stays
// coherent: one switch, whole feedback spectrum off. Patterns are short and
// specific — anything longer than ~50ms starts to feel like "error" instead
// of "tactile polish". On iOS we map our millisecond durations to the
// platform's impact-style categories (light/medium/heavy + notification
// success/warning) which feel native instead of vibrate-buzzy.
//
// Usage unchanged: haptics.tap(), haptics.slide(), haptics.merge(chainDepth), haptics.win().

const MUTE_KEY = 'tessera.audioMuted.v1';

function isMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

// Lazy-loaded Capacitor Haptics facade. Import is dynamic so web / Android
// builds don't pull the plugin code into their bundles.
type CapHaptics = {
  impact: (opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }) => Promise<void>;
  notification: (opts: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }) => Promise<void>;
  selectionStart: () => Promise<void>;
  selectionEnd: () => Promise<void>;
};

type CapHapticsModule = { Haptics: CapHaptics; ImpactStyle: Record<string, string>; NotificationType: Record<string, string> };
let _capHapticsModule: CapHapticsModule | null = null;
let _capHapticsLoading: Promise<CapHapticsModule | null> | null = null;
let _useCapacitor: boolean | null = null;

async function detectCapacitor(): Promise<boolean> {
  if (_useCapacitor !== null) return _useCapacitor;
  try {
    const { Capacitor } = await import('@capacitor/core');
    _useCapacitor = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    _useCapacitor = false;
  }
  return _useCapacitor;
}

async function getCapHaptics(): Promise<CapHapticsModule | null> {
  if (_capHapticsModule) return _capHapticsModule;
  if (!_capHapticsLoading) {
    _capHapticsLoading = import('@capacitor/haptics').then((m) => {
      _capHapticsModule = m as unknown as CapHapticsModule;
      return _capHapticsModule;
    }).catch(() => null);
  }
  return _capHapticsLoading;
}

// Fire iOS native haptic. Style maps to CoreHaptics impact/notification feel.
async function iosHaptic(kind: 'tap' | 'slide' | 'merge-1' | 'merge-2' | 'win' | 'lose') {
  const mod = await getCapHaptics();
  if (!mod) return;
  try {
    switch (kind) {
      case 'tap':     return mod.Haptics.selectionStart();
      case 'slide':   return mod.Haptics.impact({ style: 'LIGHT' as const });
      case 'merge-1': return mod.Haptics.impact({ style: 'MEDIUM' as const });
      case 'merge-2': return mod.Haptics.notification({ type: 'SUCCESS' as const });
      case 'win':     return mod.Haptics.notification({ type: 'SUCCESS' as const });
      case 'lose':    return mod.Haptics.notification({ type: 'WARNING' as const });
    }
  } catch { /* silent */ }
}

function vibrate(pattern: number | number[]): void {
  if (isMuted()) return;
  try { navigator.vibrate?.(pattern); } catch { /* ignore */ }
}

// Dispatcher — iOS goes through Capacitor, everything else through
// navigator.vibrate. Fire-and-forget for the iOS path so the web layer
// never awaits haptic feedback.
function fire(kind: 'tap' | 'slide' | 'merge-1' | 'merge-2' | 'win' | 'lose', webPattern: number | number[]): void {
  if (isMuted()) return;
  void detectCapacitor().then((useCapacitor) => {
    if (useCapacitor) {
      void iosHaptic(kind);
    } else {
      vibrate(webPattern);
    }
  });
}

export const haptics = {
  tap()   { fire('tap',   8); },
  slide() { fire('slide', 12); },
  merge(chainDepth: number) {
    if (chainDepth >= 2) fire('merge-2', [18, 40, 28]);
    else                 fire('merge-1', 16);
  },
  win()  { fire('win',  [30, 60, 30, 60, 60]); },
  lose() { fire('lose', [20, 80, 20]); },
};
